import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, Send, Ban, RotateCcw, Loader2, X, Users, Clock, CheckSquare, Square } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const DOCS = [
  ["none", "Non envoyés"], ["pending", "En attente"], ["verified", "Validés"], ["rejected", "Refusés"], ["all", "Tous"],
];
const INACTIVE = [[0, "Tous"], [30, "Inactifs 30 j"], [60, "60 j"], [90, "90 j"]];
const DOC_BADGE = {
  none: ["Rien envoyé", "bg-rose-100"], pending: ["En attente", "bg-[#FFF3CC]"],
  verified: ["Validé", "bg-[#D6F5D6]"], rejected: ["Refusé", "bg-neutral-200"],
};
const DEFAULT_TITLE = "{prenom}, il manque tes documents 📄";
const DEFAULT_MSG = "Salut {prenom} ! Pour utiliser Price en toute confiance, il nous manque tes documents. Ça prend 2 minutes : clique sur le bouton, prends tes documents en photo, et c'est envoyé !";

const ago = (iso) => {
  if (!iso) return "jamais";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "aujourd'hui" : d === 1 ? "hier" : `il y a ${d} j`;
};

function Pills({ value, options, onChange, testPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(([v, l, n]) => (
        <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testPrefix}-${v}`}
          className={`rounded-full px-3 py-1.5 text-[11px] font-black street-border-thin transition-colors ${value === v ? "bg-primary text-primary-foreground" : "bg-white"}`}>
          {l}{n !== undefined ? ` · ${n}` : ""}
        </button>
      ))}
    </div>
  );
}

// Admin : suivi documents / inactivité + relance groupée + suspension groupée.
export default function AccountsFollowupPanel() {
  const [role, setRole] = useState("driver");
  const [docs, setDocs] = useState("none");
  const [inactive, setInactive] = useState(0);
  const [suspended, setSuspended] = useState("all");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [composer, setComposer] = useState(false);
  const [suspendMode, setSuspendMode] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/price/accounts/followup", { params: { role, docs, inactive_days: inactive, suspended, q: q.trim() || undefined } });
      setData(r.data);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, [role, docs, inactive, suspended, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const items = data.items || [];
  const allOn = items.length > 0 && selected.size === items.length;
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const targets = useMemo(() => items.filter((i) => selected.has(i.user_id)).map((i) => ({ user_id: i.user_id, role: i.role })), [items, selected]);
  const c = data.counts || {};

  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin space-y-3" data-testid="accounts-followup-panel">
      <div className="flex items-center gap-2 font-[Outfit] text-[15px] font-black"><Users className="h-4 w-4 text-primary" /> Suivi des comptes</div>
      <Pills value={role} onChange={setRole} testPrefix="followup-role" options={[["driver", "Conducteurs"], ["client", "Passagers"]]} />
      <Pills value={docs} onChange={setDocs} testPrefix="followup-docs" options={DOCS.map(([v, l]) => [v, l, c[v]])} />
      <div className="flex flex-wrap items-center gap-1.5">
        <Clock className="h-3.5 w-3.5 text-foreground/50" />
        <Pills value={inactive} onChange={setInactive} testPrefix="followup-inactive" options={INACTIVE} />
      </div>
      <Pills value={suspended} onChange={setSuspended} testPrefix="followup-suspended" options={[["all", "Tous comptes"], ["no", "Actifs"], ["yes", "Suspendus"]]} />
      <label className="flex items-center gap-2 rounded-xl bg-[#FFF8F1] px-3 street-border-thin">
        <Search className="h-4 w-4 text-foreground/50" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, e-mail, téléphone, ville" data-testid="followup-search" className="h-10 flex-1 bg-transparent text-[13px] font-semibold focus:outline-none" />
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setSelected(allOn ? new Set() : new Set(items.map((i) => i.user_id)))} data-testid="followup-select-all"
          className="inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-[12px] font-black street-border-thin">
          {allOn ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />} {allOn ? "Tout décocher" : `Tout cocher (${items.length})`}
        </button>
        <div className="flex-1" />
        <button type="button" disabled={!targets.length} onClick={() => setComposer(true)} data-testid="followup-remind"
          className="street-press inline-flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-[12px] font-black text-primary-foreground street-border-thin disabled:opacity-40">
          <Send className="h-4 w-4" /> Relancer ({targets.length})
        </button>
        <button type="button" disabled={!targets.length} onClick={() => setSuspendMode("suspend")} data-testid="followup-suspend"
          className="street-press inline-flex items-center gap-1.5 rounded-xl bg-neutral-900 px-3 py-2 text-[12px] font-black text-white street-border-thin disabled:opacity-40">
          <Ban className="h-4 w-4" /> Suspendre
        </button>
        <button type="button" disabled={!targets.length} onClick={() => setSuspendMode("unsuspend")} data-testid="followup-unsuspend"
          className="street-press inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-[12px] font-black street-border-thin disabled:opacity-40">
          <RotateCcw className="h-4 w-4" /> Réactiver
        </button>
      </div>

      <div className="max-h-[420px] space-y-1.5 overflow-y-auto" data-testid="followup-list">
        {loading && <div className="py-6 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
        {!loading && items.length === 0 && <div className="py-6 text-center text-[12px] text-foreground/50">Personne dans ce filtre 🎉</div>}
        {!loading && items.map((i) => <FollowupRow key={i.user_id} i={i} on={selected.has(i.user_id)} onToggle={() => toggle(i.user_id)} />)}
      </div>

      {composer && <RemindComposer targets={targets} onClose={() => setComposer(false)} onSent={load} />}
      {suspendMode && <SuspendConfirm mode={suspendMode} targets={targets} onClose={() => setSuspendMode(null)} onDone={load} />}
    </div>
  );
}

function FollowupRow({ i, on, onToggle }) {
  const [label, cls] = DOC_BADGE[i.doc_state] || ["—", "bg-white"];
  return (
    <button type="button" onClick={onToggle} data-testid={`followup-row-${i.user_id}`}
      className={`flex w-full items-center gap-2 rounded-xl p-2.5 text-left street-border-thin ${on ? "bg-[#FFE9D6]" : "bg-secondary/30"}`}>
      {on ? <CheckSquare className="h-4 w-4 flex-none text-primary" /> : <Square className="h-4 w-4 flex-none text-foreground/40" />}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-[Outfit] text-[13px] font-black">{i.name}</span>
          {i.suspended && <span className="rounded-full bg-neutral-900 px-1.5 py-0.5 text-[9px] font-black uppercase text-white">Suspendu</span>}
        </div>
        <div className="truncate text-[10px] text-foreground/60">{i.email}{i.phone ? ` · ${i.phone}` : ""}{i.city ? ` · ${i.city}` : ""}</div>
        <div className="text-[10px] text-foreground/50">
          Actif {ago(i.last_active_at)}{i.last_reminded_at ? ` · relancé ${ago(i.last_reminded_at)} (${i.reminders_count}×)` : ""}
        </div>
      </div>
      <span className={`flex-none rounded-full px-2 py-0.5 text-[9px] font-black uppercase street-border-thin ${cls}`}>{label}</span>
    </button>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-black/50 p-3" onClick={onClose} data-testid={testid}>
      <div className="w-full max-w-md max-h-[90dvh] overflow-y-auto rounded-3xl bg-[#FFF8F1] p-5 street-border street-shadow" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function RemindComposer({ targets, onClose, onSent }) {
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [message, setMessage] = useState(DEFAULT_MSG);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/price/accounts/remind-documents", { targets, title, message });
      toast.success(`Relance envoyée à ${r.data.queued} personne(s) — notification + e-mail avec lien personnel`);
      onSent(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="followup-composer">
      <div className="flex items-center justify-between">
        <div className="font-[Outfit] text-[17px] font-black">Relancer {targets.length} personne(s)</div>
        <button type="button" onClick={onClose} data-testid="followup-composer-close" className="grid h-9 w-9 place-items-center rounded-xl bg-white street-border-thin"><X className="h-4 w-4" /></button>
      </div>
      <p className="mt-1 text-[11px] text-foreground/60">Notification + e-mail depuis contact@pricecovoit.fr. Chacun reçoit son lien personnel (30 j, sans connexion). <b>{"{prenom}"}</b> est remplacé automatiquement.</p>
      <label className="mt-3 block text-[11px] font-black uppercase tracking-widest text-foreground/50">Titre</label>
      <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} data-testid="followup-composer-title" className="mt-1 h-11 w-full rounded-xl bg-white px-3 text-[14px] font-bold street-border-thin focus:outline-none" />
      <label className="mt-3 block text-[11px] font-black uppercase tracking-widest text-foreground/50">Message</label>
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} maxLength={2000} data-testid="followup-composer-message" className="mt-1 w-full rounded-xl bg-white p-3 text-[13px] font-medium street-border-thin focus:outline-none" />
      <div className="mt-3 rounded-xl bg-white p-3 text-[12px] street-border-thin" data-testid="followup-composer-preview">
        <div className="font-black">{title.replaceAll("{prenom}", "Sarah")}</div>
        <div className="mt-1 whitespace-pre-wrap text-foreground/70">{message.replaceAll("{prenom}", "Sarah")}</div>
        <div className="mt-2 inline-block rounded-lg bg-primary px-3 py-1.5 text-[11px] font-black text-primary-foreground">Envoyer mes documents</div>
      </div>
      <button type="button" disabled={busy || title.trim().length < 2 || message.trim().length < 2} onClick={send} data-testid="followup-composer-send"
        className="street-press mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-3.5 font-[Outfit] text-[15px] font-black text-primary-foreground street-border street-shadow disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer la relance
      </button>
    </Modal>
  );
}

function SuspendConfirm({ mode, targets, onClose, onDone }) {
  const [reason, setReason] = useState("Documents non fournis");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const suspend = mode === "suspend";
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/price/accounts/bulk-suspend", { targets, action: mode, reason, notify });
      toast.success(`${r.data.updated} compte(s) ${suspend ? "suspendu(s)" : "réactivé(s)"}`);
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="followup-suspend-confirm">
      <div className="font-[Outfit] text-[17px] font-black">{suspend ? "Suspendre" : "Réactiver"} {targets.length} compte(s) ?</div>
      <p className="mt-1 text-[12px] text-foreground/60">
        {suspend ? "Passagers : connexion bloquée. Conducteurs : plus aucune course ne peut être acceptée." : "Les comptes retrouvent un accès normal."}
      </p>
      {suspend && (
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="Motif" data-testid="followup-suspend-reason"
          className="mt-3 h-11 w-full rounded-xl bg-white px-3 text-[13px] font-bold street-border-thin focus:outline-none" />
      )}
      <button type="button" onClick={() => setNotify((v) => !v)} data-testid="followup-suspend-notify" className="mt-3 flex items-center gap-2 text-[12px] font-bold">
        {notify ? <CheckSquare className="h-4 w-4 text-primary" /> : <Square className="h-4 w-4" />} Prévenir par e-mail
      </button>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button type="button" onClick={onClose} data-testid="followup-suspend-cancel" className="rounded-xl bg-white py-3 text-[13px] font-black street-border-thin">Annuler</button>
        <button type="button" disabled={busy} onClick={go} data-testid="followup-suspend-go"
          className={`street-press inline-flex items-center justify-center gap-1.5 rounded-xl py-3 text-[13px] font-black street-border-thin disabled:opacity-50 ${suspend ? "bg-neutral-900 text-white" : "bg-primary text-primary-foreground"}`}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirmer
        </button>
      </div>
    </Modal>
  );
}
